import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';

export type SponsoredCard = Schemas['SponsoredContentDto'];
export type SponsoredInput = Schemas['UpdateSponsoredContentRequest'];

export const sponsoredKeys = {
  all: ['sponsored'] as const,
  list: () => [...sponsoredKeys.all, 'list'] as const,
};

/** Every card, in the order the app's carousel shows them. Images are sized for the card on a 2x screen. */
export const sponsoredQuery = () =>
  queryOptions({
    queryKey: sponsoredKeys.list(),
    queryFn: () =>
      unwrap(api.GET('/api/sponsored-content', { params: { query: { imageWidth: 335, dpr: 2 } } })),
    staleTime: 30_000,
  });

function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: sponsoredKeys.all });
}

export function useCreateSponsored() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: SponsoredInput & { backgroundImageId: string }) =>
      unwrap(api.POST('/api/sponsored-content', { body: input })).then((r) => r.id),
    onSettled: refresh,
  });
}

/** Leave out `backgroundImageId` to keep the card's image. */
export function useUpdateSponsored() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SponsoredInput }) =>
      unwrap(api.PUT('/api/sponsored-content/{id}', { params: { path: { id } }, body: input })),
    onSettled: refresh,
  });
}

export function useDeleteSponsored() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/sponsored-content/{id}', { params: { path: { id } } })),
    onSettled: refresh,
  });
}
